import React, { ReactElement, ReactNode, useCallback, useMemo, useState, Fragment } from "react";

import { colorToAlpha, filterTruthy, formatNum, formatTokenAmount, formatUsd } from "@bridgesplit/utils";
import {
    Divider,
    PopoverOrigin,
    Stack,
    Popover as MuiPopover,
    LinearProgress as MuiLinearProgress,
    LinearProgressProps,
    keyframes,
    useMediaQuery,
    Box,
    popoverClasses,
    Collapse,
    SxProps
} from "@mui/material";
import { ArrowForwardIos, ArrowForwardOutlined, RefreshOutlined, ExpandMore } from "@mui/icons-material";

import { MediaQuery, SxType } from "../types";
import { Column, MediaStack, OutlinedCard, Row } from "./containers";
import { Icon } from "./icons";
import { TagColor, Text, TextColor, TextSkeleton, TextVariant, TooltipText } from "./typography";
import { ConditionalLink, TextButton, ToggleChevron } from "./buttons";
import { BORDER_RADIUS, FONT_SIZES, SPACING, useAppPalette, useColorFromTag } from "../theme";
import { CustomTooltipProps, Tooltip } from "./misc";
import { MEDIA } from "../util";
import { overflowScrollSx } from "../constants";

export type StatRawValue = ReactElement | string | number | undefined;

export type StatDirection = "row" | "row-reverse" | "column-reverse" | "column";
export type LoadingStyle = "both" | "value-only";
export interface StatProps {
    value: StatRawValue | [StatRawValue, StatRawValue];
    caption: string | JSX.Element;
    tooltip?: string;
    variant?: TextVariant;
    captionVariant?: TextVariant;
    captionColor?: TextColor;
    direction?: StatDirection;
    isUsd?: boolean;
    symbol?: string;
    valuePostfix?: string;
    href?: string;
    loading?: boolean;
    loadingStyle?: LoadingStyle;
    hide?: boolean;
    sx?: SxType;
    loadingWidth?: string;
    textColor?: TextColor;
    translate?: "yes" | "no";
}
export type SignMode = "pos" | "neg" | "auto" | "negOnly";

export type PairValueProps = {
    value: [StatRawValue, StatRawValue] | StatRawValue;
    tooltip?: string;
    color?: TextColor;
    variant?: TextVariant;
    align?: "flex-start" | "center" | "flex-end";
    secondaryVariant?: TextVariant;
    secondaryColor?: TextColor;
    loading?: boolean;
    direction?: "row" | "column";
    gap?: number;
    signed?: SignMode;
    secondarySigned?: SignMode;
    hideMobile?: boolean;
};

export function PairValue({
    value,
    tooltip,
    color = "body",
    variant = "body1",
    secondaryVariant = "body1",
    secondaryColor = "caption",
    loading,
    direction = "row",
    align,
    gap = 0.5,
    signed,
    secondarySigned,
    hideMobile = false
}: PairValueProps) {
    const isMobile = useMediaQuery(MEDIA.SM.below);

    if (!Array.isArray(value) || value === undefined) {
        const resolvedValue = applySign(value, signed);
        const resolvedValueColor = resolveSignedColor(value, signed, color);

        return (
            <Tooltip title={tooltip}>
                <Text color={resolvedValueColor} variant={variant} translate="no" loading={value === undefined}>
                    {resolvedValue}
                </Text>
            </Tooltip>
        );
    }

    const resolvedValue = applySign(value[0], signed);
    const resolvedValueColor = resolveSignedColor(value[0], signed, color);
    const resolvedSecondary = applySign(value[1], secondarySigned);
    const resolvedSecondaryColor = resolveSignedColor(value[1], secondarySigned, secondaryColor);
    return (
        <Stack
            sx={{
                alignItems: align ?? (direction === "row" ? "center" : "flex-start"),
                gap: direction === "row" ? `${gap * SPACING}px` : undefined,
                flexDirection: direction
            }}
        >
            <Tooltip title={tooltip}>
                <Text color={resolvedValueColor} variant={variant} translate="no" loading={resolvedValue === undefined}>
                    {resolvedValue}
                </Text>
            </Tooltip>
            {!(isMobile && hideMobile) && (
                <Text
                    variant={secondaryVariant}
                    color={resolvedSecondaryColor}
                    translate="no"
                    loading={resolvedSecondary === undefined}
                >
                    {resolvedSecondary}
                </Text>
            )}
        </Stack>
    );
}

function detectSign(val: StatRawValue): number {
    if (val === undefined || val === null) return 0;
    if (typeof val === "number") return val > 0 ? 1 : val < 0 ? -1 : 0;
    if (typeof val === "string") {
        const stripped = val.replace(/[^0-9.\-+]/g, "");
        const num = parseFloat(stripped);
        if (isNaN(num)) return 0;
        return num > 0 ? 1 : num < 0 ? -1 : 0;
    }
    return 0;
}

function resolveSign(val: StatRawValue, mode: SignMode): number {
    switch (mode) {
        case "pos":
            return 1;
        case "neg":
            return -1;
        case "auto":
            return detectSign(val);
        case "negOnly": {
            const detected = detectSign(val);
            return detected < 0 ? -1 : 0;
        }
    }
}

// Prepend +/- sign if needed, avoiding double-signing.
function applySign(val: StatRawValue, mode?: SignMode): StatRawValue {
    if (!mode || val === undefined) return val;

    const sign = resolveSign(val, mode);
    if (sign === 0) return val;

    const str = typeof val === "number" ? String(val) : typeof val === "string" ? val : undefined;
    if (!str) return val;

    const alreadySigned = str.startsWith("+") || str.startsWith("-");
    if (sign > 0) return alreadySigned ? str : `+${str}`;
    if (sign < 0) return alreadySigned ? str : `-${str}`;
    return val;
}

function resolveSignedColor(val: StatRawValue, mode: SignMode | undefined, fallback: TextColor): TextColor {
    if (!mode) return fallback;

    const sign = resolveSign(val, mode);
    if (sign > 0) return "success";
    if (sign < 0) return "error";
    return fallback;
}

export function Stat({
    value,
    caption,
    tooltip,
    href,
    variant = "h4",
    loading,
    valuePostfix,
    isUsd,
    sx,
    hide,
    symbol,
    loadingWidth = "90px",
    loadingStyle = "both",
    direction = "column",
    textColor,
    captionVariant,
    captionColor,
    translate = "yes"
}: StatProps) {
    const isRow = direction.includes("row");
    const isHeading = variant.toString().includes("h");

    const formatStat = useCallback(
        (value: StatRawValue, options?: { hideSymbol?: boolean }) => {
            const postFix = valuePostfix ? ` ${valuePostfix}` : "";

            if (typeof value === "number" || value === undefined) {
                if (isUsd) {
                    return formatUsd(value) + postFix;
                }
                if (symbol) {
                    return formatTokenAmount(value, { symbol, hideSymbol: options?.hideSymbol }) + postFix;
                }
                return formatNum(value) + postFix;
            }
            return value;
        },
        [isUsd, symbol, valuePostfix]
    );

    const valueContent = useMemo(() => {
        if (loading) {
            return <TextSkeleton width={loadingWidth} variant={variant} />;
        }

        if (Array.isArray(value)) {
            return (
                <Row sx={{ minWidth: "max-content" }} spacing={0.5}>
                    <Text variant={variant} translate={translate}>
                        {formatStat(value[0], { hideSymbol: true })}
                    </Text>
                    <Text color="disabled" variant="body2">
                        <ArrowForwardOutlined />
                    </Text>
                    <Text variant={variant} translate={translate}>
                        {formatStat(value[1])}
                    </Text>
                </Row>
            );
        }
        if (["undefined", "string", "number"].includes(typeof value)) {
            const formattedValue = formatStat(value);
            return (
                <Text
                    translate={translate}
                    loadingWidth={loadingWidth}
                    loading={loading}
                    color={textColor}
                    onClick={href ? () => window.open(href) : undefined}
                    sx={{
                        cursor: href ? "pointer" : undefined,
                        ":hover": { textDecoration: href ? "underline" : undefined }
                    }}
                    variant={variant}
                >
                    {formattedValue}
                    {href && <Icon type="url" />}
                </Text>
            );
        }

        return value;
    }, [formatStat, href, loading, loadingWidth, textColor, translate, value, variant]);

    const captionContent = useMemo(() => {
        if (loading && loadingStyle !== "value-only") return <TextSkeleton width="50px" variant={variant} />;
        if (typeof caption !== "string") return caption;
        return (
            <TooltipText
                loadingWidth="50px"
                helpText={tooltip}
                color={captionColor ?? "caption"}
                variant={(() => {
                    if (captionVariant) return captionVariant;
                    if (isRow) return "body1";
                    if (isHeading) return "body2";
                    return variant;
                })()}
            >
                {caption}
            </TooltipText>
        );
    }, [loading, loadingStyle, variant, caption, tooltip, captionColor, captionVariant, isRow, isHeading]);

    if (hide) return null;

    return (
        <Stack
            sx={{
                justifyContent: direction === "row-reverse" ? "flex-end" : undefined,
                flexDirection: direction,
                gap: isRow ? `${SPACING / 2}px` : undefined,
                ...sx
            }}
        >
            {captionContent}
            {valueContent}
        </Stack>
    );
}

export type StatContainer = {
    spacing?: number;
    sx?: SxType;
    stats: Omit<StatProps, "direction" | "variant" | "loading" | "loadingStyle">[] | undefined;
    variant?: TextVariant;
    loading?: boolean;
    loadingStyle?: LoadingStyle;
    captionVariant?: TextVariant;
    captionColor?: TextColor;
    translate?: "yes" | "no";
};
export type StatContainerExpandable = {
    spacing?: number;
    sx?: SxType;
    stat: Omit<StatProps, "direction" | "variant" | "loading" | "loadingStyle"> | undefined;
    nestedStats: Omit<StatProps, "direction" | "variant" | "loading" | "loadingStyle">[] | undefined;
    variant?: TextVariant;
    loading?: boolean;
    loadingStyle?: LoadingStyle;
    captionVariant?: TextVariant;
    captionColor?: TextColor;
    translate?: "yes" | "no";
};

export function StatRow({
    stats,
    spacing = 3,
    sx,
    direction,
    loading,
    variant,
    loadingStyle,
    captionVariant,
    captionColor,
    translate = "yes"
}: StatContainer & { direction?: StatDirection }) {
    if (!stats?.some((s) => !s.hide)) return null;

    return (
        <Row sx={{ gap: spacing, alignItems: "flex-start", flexWrap: "wrap", ...sx }}>
            {stats?.map((s, i) => (
                <Stat
                    loadingStyle={loadingStyle}
                    loading={loading}
                    variant={variant}
                    captionVariant={captionVariant}
                    captionColor={captionColor}
                    translate={translate}
                    key={typeof s.caption === "string" ? s.caption + i : i}
                    {...s}
                    direction={direction}
                />
            ))}
        </Row>
    );
}

export function StatColumn({
    sx,
    stats,
    spacing = 0.5,
    loading,
    variant = "body1",
    loadingStyle,
    captionVariant,
    translate = "yes"
}: StatContainer) {
    if (!stats?.some((s) => !s.hide)) return null;
    const statColumnSx = { ...overflowScrollSx, ...sx } as SxProps;
    return (
        <Column spacing={spacing} sx={statColumnSx}>
            {stats?.map((s, i) => (
                <Stat
                    translate={translate}
                    captionVariant={variant ?? captionVariant}
                    loadingStyle={loadingStyle}
                    variant={variant}
                    loading={loading}
                    sx={{ justifyContent: "space-between", gap: 2 }}
                    direction="row"
                    key={i}
                    {...s}
                />
            ))}
        </Column>
    );
}
export function StatColumnExpandable({
    sx,
    stat,
    nestedStats,
    spacing = 1,
    loading,
    variant = "body1",
    loadingStyle,
    captionVariant,
    translate = "yes"
}: StatContainerExpandable) {
    const [expanded, setExpanded] = useState<boolean>(false);
    if (!stat || stat.hide) return null;
    const expandEnabled = nestedStats && nestedStats.some((s) => !s.hide);

    const toggleExpanded = () => {
        setExpanded((prev) => !prev);
    };

    return (
        <Column
            spacing={0}
            sx={{
                minHeight: 0,
                overflow: "scroll",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                ...sx
            }}
        >
            <Row
                sx={expandEnabled ? { cursor: "pointer" } : {}}
                onClick={expandEnabled ? () => toggleExpanded() : undefined}
                spacing={1}
            >
                <Stat
                    translate={translate}
                    captionVariant={variant ?? captionVariant}
                    loadingStyle={loadingStyle}
                    variant={variant}
                    loading={loading}
                    sx={{ justifyContent: "space-between", flexGrow: 1 }}
                    direction="row"
                    {...stat}
                />
                {expandEnabled && !loading && (
                    <ToggleChevron textVariant="body1" expanded={expanded} sx={{ padding: 0 }} />
                )}
            </Row>
            <Collapse in={expanded}>
                <Column spacing={spacing} sx={{ paddingTop: `${spacing * SPACING}px` }}>
                    <Divider />
                    <StatColumn spacing={spacing} variant={variant} stats={nestedStats} />
                </Column>
            </Collapse>
        </Column>
    );
}

export function MediaStats({
    stats,
    mobileSpacing = 0.5,
    spacing = 3,
    query = MEDIA.LG,
    captionVariant,
    loading,
    loadingStyle,
    variant
}: StatContainer & { query?: MediaQuery; mobileSpacing?: number }) {
    const isAbove = useMediaQuery(query.above);

    return (
        <MediaStack query={query} mobileSpacing={mobileSpacing} spacing={spacing}>
            {stats?.map((s, i) => (
                <Stat
                    captionVariant={captionVariant ?? (isAbove ? "body2" : "body1")}
                    loadingStyle={loadingStyle}
                    variant={variant ?? (isAbove ? "h4" : "body1")}
                    loading={loading}
                    sx={{
                        [query.below]: { flexDirection: "row", justifyContent: "space-between", gap: 2 },
                        [query.above]: { flexDirection: "column", gap: 0 }
                    }}
                    direction="row"
                    key={i}
                    {...s}
                />
            ))}
        </MediaStack>
    );
}

export interface BreadcrumbsInterface {
    text: string | undefined;
    path?: string;
}
export function Breadcrumbs({ paths, loading }: { paths: BreadcrumbsInterface[]; loading?: boolean }) {
    return (
        <Row wrap>
            {paths.map((s, i) => {
                const isLastIndex = i === paths.length - 1;
                if (s.path && !isLastIndex) {
                    return (
                        <ConditionalLink key={i} to={s.path}>
                            <BreadcrumbsText isLink loading={loading} text={s.text} isLastIndex={isLastIndex} />
                        </ConditionalLink>
                    );
                }
                return (
                    <BreadcrumbsText
                        isLink={!!s.path}
                        key={i}
                        loading={loading}
                        text={s.text}
                        isLastIndex={isLastIndex}
                    />
                );
            })}
        </Row>
    );
}
function BreadcrumbsText({
    text,
    isLastIndex,
    loading,
    isLink
}: {
    text: string | undefined;
    isLastIndex: boolean;
    loading?: boolean;
    isLink: boolean;
}) {
    return (
        <Row>
            <Text
                loading={!text || loading}
                underlineLink="hover"
                isLink={!isLastIndex && isLink}
                color={isLastIndex ? "body" : "caption"}
                variant={isLastIndex ? "h4" : "body1"}
            >
                {text}
            </Text>

            {!isLastIndex && (
                <Text variant="body2" color="disabled">
                    <ArrowForwardIos sx={{ mx: "5px" }} color="inherit" fontSize="inherit" />
                </Text>
            )}
        </Row>
    );
}

interface ProgressBarProps extends LinearProgressProps {
    loading?: boolean;
    bgColor?: string;
}
export function ProgressBar({ variant = "determinate", sx, loading, value, bgColor, ...props }: ProgressBarProps) {
    const { divider } = useAppPalette();
    return (
        <MuiLinearProgress
            variant="determinate"
            value={loading ? 0 : value}
            sx={{
                width: "100%",
                height: "8px",
                backgroundColor: divider,
                "& .MuiLinearProgress-bar": {
                    backgroundColor: bgColor
                },
                ...sx
            }}
            {...props}
        />
    );
}

export function usePopover() {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    function open(event: React.MouseEvent<HTMLElement>) {
        // prevents double click
        if (event.detail === 0) return;
        setAnchorEl(event.currentTarget);
    }

    function close() {
        setAnchorEl(null);
    }

    const popoverProps: PopoverProps = { anchorEl, close };

    return { open, close, isOpen: !!anchorEl, anchorEl, popoverProps };
}

export type PopoverProps = {
    anchorEl: HTMLElement | null;
    close: () => void;
};

export type UsePopoverResult = ReturnType<typeof usePopover>;

type PopoverWrapperProps = {
    children: ReactNode;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
    sx?: SxType;
};

export const PopoverWrapper = ({
    children,
    anchorOrigin,
    transformOrigin,
    sx,
    anchorEl,
    close
}: PopoverWrapperProps & PopoverProps) => {
    const { border, cardBackground, background } = useAppPalette();

    const columnSx = {
        overflow: "scroll",
        border,
        background: cardBackground,
        borderRadius: BORDER_RADIUS,
        ...sx
    };

    const popoverSx = {
        [`.${popoverClasses.paper}`]: {
            background: background,
            backgroundImage: "none",
            borderRadius: BORDER_RADIUS
        }
    };

    return (
        <MuiPopover
            sx={popoverSx}
            anchorOrigin={
                anchorOrigin ?? {
                    vertical: "bottom",
                    horizontal: "center"
                }
            }
            transformOrigin={
                transformOrigin ?? {
                    vertical: "top",
                    horizontal: "center"
                }
            }
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={close}
        >
            <Column sx={columnSx}>{children}</Column>
        </MuiPopover>
    );
};

export interface MenuItemProps {
    icon?: ReactNode;
    onClick?: () => void;
    text: string;
    isDangerous?: boolean;
    hide?: boolean;
    rowEnd?: ReactNode;
    disableClose?: boolean;
    disabled?: boolean;
    selected?: boolean;
}

export interface MenuItemsProps {
    px?: number;
    py?: number;
    items: MenuItemProps[];
    close: () => void;
    sx?: SxType;
    spacing?: number;
    iconWidth?: string;
}
export function MenuItems({ items, ...props }: MenuItemsProps) {
    const activeItems = items.filter((i) => !i.hide);

    if (!activeItems.length) return null;

    return (
        <Column role="menu" sx={{ py: 0.5, px: 0.5, ...props.sx }}>
            {activeItems.map((item, i) => (
                <MenuItem key={i} item={item} {...props} />
            ))}
        </Column>
    );
}

export function SectionedMenuItems({ items, ...props }: Omit<MenuItemsProps, "items"> & { items: MenuItemProps[][] }) {
    const activeSections = items
        .map((section) => section.filter((item) => !item.hide))
        .filter((section) => section.length);
    if (!activeSections.length) return null;

    return (
        <Column role="menu" sx={{ py: 0.5, ...props.sx }}>
            {activeSections.map((section, i) => (
                <Fragment key={i}>
                    <Column px={0.5}>
                        {section.map((item, j) => (
                            <MenuItem key={j} item={item} px={props.px} py={props.py} {...props} />
                        ))}
                    </Column>
                    {i !== activeSections.length - 1 && <Divider sx={{ my: 1 }} />}
                </Fragment>
            ))}
        </Column>
    );
}

export function MenuItem({ item, ...props }: { item: MenuItemProps } & Omit<MenuItemsProps, "items">) {
    const { hoverBackground, error } = useAppPalette();

    const { isDangerous, rowEnd, disableClose, onClick, disabled, icon, selected, text } = item;
    const { close, px = 1, py = 1, spacing = 1, iconWidth = FONT_SIZES.body2 } = props;
    return (
        <Row
            role="menuitem"
            aria-disabled={disabled || undefined}
            tabIndex={0}
            spaceBetween
            sx={{
                cursor: onClick && !disabled ? "pointer" : undefined,
                "&:hover":
                    onClick && !disabled
                        ? {
                              backgroundColor: isDangerous ? colorToAlpha(error, 0.2) : hoverBackground
                          }
                        : undefined,
                px,
                py,
                background: isDangerous ? colorToAlpha(error, 0.1) : selected ? hoverBackground : undefined,
                borderRadius: BORDER_RADIUS
            }}
            onClick={() => {
                if (disabled) return;
                if (onClick) {
                    onClick?.();
                    if (!disableClose) {
                        close();
                    }
                }
            }}
        >
            <Row spacing={spacing}>
                {icon && (
                    <Text
                        sx={{ width: iconWidth, justifyContent: "center" }}
                        color={(() => {
                            if (disabled) return "disabled";
                            if (isDangerous) return "error";
                            return "body";
                        })()}
                    >
                        {icon}
                    </Text>
                )}
                <Text
                    color={(() => {
                        if (disabled) return "disabled";
                        if (isDangerous) return "error";
                        return "body";
                    })()}
                >
                    {text}
                </Text>
            </Row>
            {rowEnd}
        </Row>
    );
}

export function TagWrapper({ color = "info", sx, children }: { color?: TagColor; sx?: SxType; children: ReactNode }) {
    const baseColor = useColorFromTag(color);
    const { isDarkMode } = useAppPalette();

    return (
        <Box
            px={1.5}
            py={1}
            sx={{
                backgroundColor: colorToAlpha(baseColor, isDarkMode ? 0.2 : 0.1),
                borderRadius: BORDER_RADIUS,
                ...sx
            }}
        >
            {children}
        </Box>
    );
}

export function RefreshButtonWithLoading({
    callback,
    isFetching,
    variant,
    color = "caption"
}: {
    callback: () => void;
    isFetching: boolean;
    variant?: TextVariant;
    color?: TextColor;
}) {
    return (
        <TextButton
            onClick={callback}
            variant={variant}
            disabled={isFetching}
            key={isFetching ? "fetching" : "not-fetching"}
            color={color}
        >
            <RefreshOutlined
                sx={{
                    animation: `${rotate} 1s linear infinite`,
                    animationPlayState: isFetching ? "running" : "paused"
                }}
            />
        </TextButton>
    );
}

const rotate = keyframes`
0% {
    transform: rotate(0deg);
}
100%{
    transform: rotate(360deg);
}
`;

export type StatCardProps = {
    value: StatRawValue;
    caption: string;
    description?: string;
    descriptionTooltip?: string;
    tooltip?: ReactNode;
    loading?: boolean;
    query?: MediaQuery;
    hide?: boolean;
    disableDesktopInset?: boolean;
    captionVariant?: TextVariant;
    variant?: TextVariant;
    valueColor?: TextColor;
    valueCharCount?: number; // To compute content width to avoid resizing cards for dynamic values
};

const VARIANT_CHAR_WIDTH: Record<TextVariant, number> = {
    h1: 18,
    h2: 16,
    h3: 13,
    h4: 11,
    body1: 10,
    body2: 9,
    caption: 8
};

function getComputedWidth(value: StatRawValue, variant: TextVariant, valueCharCount: number | undefined) {
    const charWidth = VARIANT_CHAR_WIDTH[variant];
    if (valueCharCount) return valueCharCount * charWidth;
    if (!value || !["string", "number"].includes(typeof value)) return 5 * charWidth;
    return value.toString().length * charWidth;
}

export function StatCard({
    query = MEDIA.MD,
    disableDesktopInset,
    variant = "h4",
    captionVariant = "caption",
    ...otherProps
}: StatCardProps) {
    const { cardBackground, border } = useAppPalette();
    return (
        <Column
            sx={{
                gap: 0,
                [query.above]: disableDesktopInset ? {} : { width: `calc(100% - ${SPACING * 2}px)` },
                p: 1.5,
                background: cardBackground,
                border,
                borderRadius: BORDER_RADIUS
            }}
        >
            <StatCardContent {...{ variant, captionVariant, ...otherProps }} />
        </Column>
    );
}

export function StatCardContent(props: StatCardProps) {
    const {
        value,
        caption,
        variant = "h4",
        captionVariant,
        description,
        tooltip,
        descriptionTooltip,
        loading,
        valueColor,
        valueCharCount
    } = props;
    const minWidth = getComputedWidth(value, variant, valueCharCount);
    return (
        <Column gap={0.25} minWidth={minWidth}>
            <Text loading={loading} loadingWidth="40px" variant={captionVariant} color="caption">
                {caption}
            </Text>
            {typeof value === "string" ? (
                <Text
                    color={valueColor}
                    variant={variant}
                    sx={{ svg: { fontSize: FONT_SIZES.body1 + "px", color: "disabled" } }}
                    loading={loading}
                    loadingWidth="60px"
                >
                    {value}
                </Text>
            ) : (
                value
            )}
            <Tooltip placement="right" reverseColors={typeof tooltip !== "string"} title={tooltip}>
                <TooltipText
                    loadingWidth="30px"
                    loading={loading}
                    helpText={descriptionTooltip}
                    variant={captionVariant}
                    color="caption"
                >
                    {description}{" "}
                    {tooltip && (
                        <Icon
                            type="tooltip"
                            sx={{
                                width: "16px"
                            }}
                        />
                    )}
                </TooltipText>
            </Tooltip>
        </Column>
    );
}

const MIN_CARD_WIDTH = 260;

export function StatCards({
    stats: statsRaw,
    variant = "h4",
    captionVariant = "body2",
    query = MEDIA.MD,
    loading: propsLoading,
    spacing = 2,
    columns,
    disableDesktopInset
}: {
    stats: StatCardProps[];
    variant?: TextVariant;
    captionVariant?: TextVariant;
    query?: MediaQuery;
    loading?: boolean;
    spacing?: number;
    /** Max columns per row on desktop. When set, uses grid layout instead of single row. */
    columns?: number;
    /** When true, cards use full column width on desktop (no built-in inset). */
    disableDesktopInset?: boolean;
}) {
    const stats = statsRaw.filter((c) => !c.hide);
    if (!stats.length) return null;

    const isMultipleOfThree = stats.length % 3 === 0;
    const gridColumns = columns
        ? `repeat(${columns}, 1fr)`
        : isMultipleOfThree
        ? `repeat(auto-fit, minmax(${MIN_CARD_WIDTH}px, 1fr))`
        : "1fr";

    return (
        <Row
            spacing={spacing}
            sx={{
                alignItems: "stretch",
                ...(columns
                    ? {
                          display: "grid",
                          gap: `${spacing * SPACING}px`,
                          gridTemplateColumns: gridColumns,
                          [query.below]: {
                              gridTemplateColumns: isMultipleOfThree
                                  ? `repeat(auto-fit, minmax(${MIN_CARD_WIDTH}px, 1fr))`
                                  : "1fr"
                          }
                      }
                    : {
                          flexWrap: "nowrap",
                          [query.below]: {
                              display: "grid",
                              gap: `${spacing * SPACING}px`,
                              gridTemplateColumns: gridColumns
                          }
                      })
            }}
        >
            {stats.map(({ loading, ...card }, i) => (
                <StatCard
                    key={i}
                    query={query}
                    loading={loading || propsLoading}
                    disableDesktopInset={disableDesktopInset}
                    variant={variant}
                    captionVariant={captionVariant}
                    {...card}
                />
            ))}
        </Row>
    );
}

export type QuestionToggleProps = { label: string; answer: JSX.Element | string[] };
export function QuestionToggleCards({
    questions,
    labelVariant
}: {
    questions: QuestionToggleProps[];
    labelVariant?: TextVariant;
}) {
    const [expandedIndex, setExpandedIndex] = useState<number | undefined>(undefined);
    return (
        <Column spacing={1}>
            {questions.map(({ label, answer }, i) => (
                <Column
                    onClick={() => setExpandedIndex(expandedIndex === i ? undefined : i)}
                    key={i}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expandedIndex === i}
                    aria-controls={`faq-answer-${i}`}
                    sx={{ cursor: "pointer" }}
                >
                    <OutlinedCard padding={2}>
                        <Row spaceBetween>
                            <Text variant={labelVariant}>{label}</Text>
                            <TextButton variant="h3" color="disabled">
                                <ExpandMore
                                    sx={{
                                        transform: expandedIndex === i ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.3s ease"
                                    }}
                                />
                            </TextButton>
                        </Row>
                        <Collapse in={expandedIndex === i}>
                            <Column spacing={1} sx={{ marginTop: 1 }}>
                                {Array.isArray(answer)
                                    ? answer.map((a, j) => (
                                          <Text key={j} color="caption">
                                              {a}
                                          </Text>
                                      ))
                                    : answer}
                            </Column>
                        </Collapse>
                    </OutlinedCard>
                </Column>
            ))}
        </Column>
    );
}

export type TooltipSection =
    | { type: "divider" }
    | {
          type: "render";
          render: () => ReactElement | null;
          px?: number;
          py?: number;
          spacing?: number;
      };

type ExpandedTooltipProps = {
    sections: TooltipSection[];
    spacing?: number;
    py?: number;
    px?: number;
    width?: string;
    tooltipProps?: Partial<CustomTooltipProps>;
    children?: React.ReactNode;
};

function TooltipSectionRenderer({ section }: { section: TooltipSection }) {
    if (section.type === "divider") {
        return <Divider />;
    }

    return (
        <Column px={section.px ?? 1.5} py={section.py} spacing={section.spacing ?? 0.5}>
            {section.type === "render" ? section.render() : null}
        </Column>
    );
}

export function ExpandedTooltip({
    sections,
    spacing = 1,
    py = 1.5,
    px = 0,
    width = "100%",
    tooltipProps = {},
    children
}: ExpandedTooltipProps) {
    const hasSections = sections?.length > 0;

    const content = hasSections ? (
        <Column spacing={spacing} py={py} px={px} sx={{ width, minWidth: "250px" }}>
            {sections.filter(filterTruthy).map((sec, i) => (
                <TooltipSectionRenderer key={i} section={sec} />
            ))}
        </Column>
    ) : null;

    const displayValue = children ?? null;

    return (
        <Tooltip noPadding title={content ?? null} reverseColors {...tooltipProps}>
            {React.isValidElement(displayValue) ? displayValue : <>{displayValue}</>}
        </Tooltip>
    );
}
